import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import { Container, Typography, Stack, IconButton, InputAdornment, TextField, Link, CircularProgress } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api';
import Logo from '../logo/Logo';
import Iconify from '../iconify/Iconify';
import useResponsive from '../../hooks/useResponsive';

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: theme.customShadows.card,
  backgroundColor: theme.palette.background.default,
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

export default function SignupComponent() {
  const mdUp = useResponsive('up', 'md');
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [otpMode, setOtpMode] = useState(false);
  const [registerStep, setRegisterStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [account, setAccount] = useState({
    email: '',
    password: '',
    password_confirmation: '',
    name: '',
    role: 'admin',
    gstNo: '',
    address: '',
    city: '',
    district: '',
    state: '',
    country: '',
    contactNo: '',
    whatsappNo: '',
    pinCode: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAccount({ ...account, [name]: value });
  };

  const handleRegister = () => {
    if (!validateStepOne()) return;
    if (!validateStepTwo()) return;
    setShowLoader(true);
    api.post('/users', { user: account })
      .then((response) => {
        setShowLoader(false);
        if (response.data.message) {
          toast.success("OTP sent! Please verify.");
          setOtpMode(true);
        } else if (response.data.error) {
          toast.error(response.data.error);
        }
      })
      .catch(() => {
        setShowLoader(false);
        toast.error("Registration failed.");
      });
  };

  const handleVerifyOtp = () => {
    setShowLoader(true);
    api.post('/verify-otp', { email: account.email, 
        otp,
        user: account })
      .then((response) => {
        setShowLoader(false);
        if (response.data.success) {
          toast.success("OTP verified! Logging in...");
          navigate('/login');
        } else {
          toast.error("Invalid OTP. Try again.");
        }
      })
      .catch(() => {
        setShowLoader(false);
        toast.error("OTP verification failed.");
      });
  };

  const validateStepOne = () => {
    if (!account.name || !account.email || !account.password || !account.password_confirmation) {
      toast.error('All fields are compulsory.');
      return false;
    }
    if (account.password !== account.password_confirmation) {
      toast.error('Passwords do not match.');
      return false;
    }
    return true;
  };

  const validateStepTwo = () => {
    const requiredFields = ['gstNo', 'address', 'city', 'district', 'state', 'country', 'contactNo', 'whatsappNo', 'pinCode'];
    const hasMissing = requiredFields.some((field) => !account[field] || String(account[field]).trim() === '');
    if (hasMissing) {
      toast.error('All fields are compulsory.');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (!validateStepOne()) return;
    setRegisterStep(2);
  };

  const handlePreviousStep = () => {
    setRegisterStep(1);
  };

  return (
    <>
      <Helmet>
        <title>City Courier - Register</title>
      </Helmet>

      <StyledRoot>
        <Logo sx={{ position: 'fixed', top: { xs: 16, sm: 24, md: 40 }, left: { xs: 16, sm: 24, md: 40 } }} />

        {mdUp && (
          <StyledSection>
            <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
              Hi, {otpMode ? "Verify Your OTP" : "Register as Admin in City Courier Services"}
            </Typography>
            <img src="/assets/illustrations/illustration_login.png" alt="login" />
          </StyledSection>
        )}

        <Container maxWidth="sm">
          <StyledContent>
            <Typography variant="h4" gutterBottom>
              {otpMode ? "Verify OTP" : registerStep === 1 ? "Register - Step 1" : "Register - Step 2"}
            </Typography>

            <Typography variant="body2" sx={{ mb: 5 }}>
              Already have an account? <Link variant="subtitle2"  href="/login">Login</Link>
            </Typography>

            {otpMode ? (
              <Stack spacing={3} mb={3}>
                <TextField name="otp" label="Enter OTP" value={otp} onChange={(e) => setOtp(e.target.value)} />
                <LoadingButton
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={handleVerifyOtp}
                  disabled={showLoader}
                >
                  {showLoader ? <CircularProgress size={24} /> : "Verify OTP"}
                </LoadingButton>
              </Stack>
            ) : (
              <Stack spacing={3} mb={3}>
                {registerStep === 1 ? (
                  <>
                    <TextField name="name" label="Full Name" value={account.name} onChange={handleChange} />
                    <TextField name="email" label="Email address" value={account.email} onChange={handleChange} />
                    <TextField
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={account.password}
                      onChange={handleChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      name="password_confirmation"
                      label="Confirm Password"
                      type={showPassword ? 'text' : 'password'}
                      value={account.password_confirmation}
                      onChange={handleChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <LoadingButton
                      fullWidth
                      size="large"
                      variant="contained"
                      onClick={handleNextStep}
                      disabled={showLoader}
                    >
                      Next
                    </LoadingButton>
                  </>
                ) : (
                  <>
                    <TextField name="gstNo" label="GST No" value={account.gstNo} onChange={handleChange} />
                    <TextField name="address" label="Address" value={account.address} onChange={handleChange} />
                    <TextField name="city" label="City" value={account.city} onChange={handleChange} />
                    <TextField name="district" label="District" value={account.district} onChange={handleChange} />
                    <TextField name="state" label="State" value={account.state} onChange={handleChange} />
                    <TextField name="country" label="Country" value={account.country} onChange={handleChange} />
                    <TextField name="contactNo" label="Contact Number" value={account.contactNo} onChange={handleChange} />
                    <TextField name="whatsappNo" label="Whatsapp Number" value={account.whatsappNo} onChange={handleChange} />
                    <TextField name="pinCode" label="Pin Code" value={account.pinCode} onChange={handleChange} />
                    <LoadingButton
                      fullWidth
                      size="large"
                      variant="outlined"
                      onClick={handlePreviousStep}
                      disabled={showLoader}
                    >
                      Back
                    </LoadingButton>
                    <LoadingButton
                      fullWidth
                      size="large"
                      variant="contained"
                      onClick={handleRegister}
                      disabled={showLoader}
                    >
                      {showLoader ? <CircularProgress size={24} /> : "Register"}
                    </LoadingButton>
                  </>
                )}
              </Stack>
            )}
          </StyledContent>
        </Container>
      </StyledRoot>
    </>
  );
}
